type FlagMask = number;

/**
 * A utility class for tracking changes to a set of bitwise flags between frames in a game loop.
 */
export class FlagChangeTracker {
  static bitsPerFlag = 2;
  static bitStorageBits = 16;
  static allFlagBits = (1 << FlagChangeTracker.bitStorageBits) - 1;

  static maxFlags = Math.floor(FlagChangeTracker.bitStorageBits / FlagChangeTracker.bitsPerFlag);

  // Build a flag pattern that includes all the specified flags
  static flagsPatternIncluding(...flags: FlagMask[]): FlagMask {
    return flags.reduce((combinedFlag, flag) => combinedFlag | flag, 0);
  }

  // Build a flag pattern that includes all flags except the specified ones
  static flagsPatternExcluding(...flags: FlagMask[]): FlagMask {
    const excludedFlag = this.flagsPatternIncluding(...flags);
    return ~excludedFlag & FlagChangeTracker.allFlagBits;
  }

  // Allocate a new flag and return the mask pattern for that flag (both bits set to 1)
  static allocate(index: number): FlagMask {
    if (index < 0 || index >= FlagChangeTracker.maxFlags) {
      throw new Error(`Index out of bounds. Should be in the range 0-${FlagChangeTracker.bitStorageBits}.`);
    }
    const bitIndex = index * FlagChangeTracker.bitsPerFlag;
    return 0b11 << bitIndex;
  }

  // Shift this frame bit to last frame bit, masked by FlagMask
  static prepareForNewFrame(flags: number, ...flagMasks: FlagMask[]): number {
    let result = flags;
    for (const mask of flagMasks) {
      // Extract the bits within the mask, then shift them by one position
      const shifted = ((flags & mask) << 1) & mask;

      result &= ~mask; // Clear the bits within the mask

      // Combine the shifted bits with the result
      result |= shifted;
    }
    return result;
  }

  // Set a flag for this frame
  static setFlag(flags: number, flagMask: FlagMask, value: boolean): number {
    const currentFrameMask = (flagMask >> 1) & flagMask;
    let newValue = 0;
    if (value) {
      newValue = flags | currentFrameMask;
    } else {
      newValue = flags & ~currentFrameMask;
    }
    return (flags & ~flagMask) | (newValue & flagMask);
  }

  // Check if a flag was set this frame
  static isFlagSetThisFrame(flags: number, flagMask: FlagMask): boolean {
    const currentFrameMask = (flagMask >> 1) & flagMask;
    return (flags & currentFrameMask) !== 0;
  }

  // Check if a flag was set last frame
  static isFlagSetLastFrame(flags: number, flagMask: FlagMask): boolean {
    const currentFrameMask = (flagMask >> 1) & flagMask;
    const lastFrameMask = flagMask & ~currentFrameMask;
    return (flags & lastFrameMask) !== 0;
  }

  // Check if a flag was newly set this frame
  static isNewlySet(flags: number, flagMask: FlagMask): boolean {
    return (
      FlagChangeTracker.isFlagSetThisFrame(flags, flagMask) && !FlagChangeTracker.isFlagSetLastFrame(flags, flagMask)
    );
  }

  // Check if a flag was set last frame but not this frame
  static isNoLongerSet(flags: number, flagMask: FlagMask): boolean {
    return (
      !FlagChangeTracker.isFlagSetThisFrame(flags, flagMask) && FlagChangeTracker.isFlagSetLastFrame(flags, flagMask)
    );
  }
}
